/**
 * Validation utilities for lead forms
 */

import { differenceInYears, isAfter, isValid, parseISO } from 'date-fns';

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validateEmail = (email) => {
  if (!email) return null;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }

  return null;
};

/**
 * Validates phone number in E.164 format
 * @param {string} phone - Phone number to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validatePhoneNumber = (phone) => {
  if (!phone) return null;

  const trimmedPhone = phone.trim();

  // Must start with +
  if (!trimmedPhone.startsWith('+')) {
    return 'Phone number must be in E.164 format starting with + (e.g., +15551234567)';
  }

  // Check for any non-digit characters after the +
  const withoutPlus = trimmedPhone.slice(1);
  if (!/^\d+$/.test(withoutPlus)) {
    return 'Phone number must contain only digits after the + symbol (no parentheses, dashes, or spaces)';
  }

  // Check total length limits per E.164 standard
  if (trimmedPhone.length < 8) { // Minimum: +1234567 (7 digits)
    return 'Phone number is too short. Must be at least 7 digits after the + symbol';
  }

  if (trimmedPhone.length > 16) { // Maximum: +123456789012345 (15 digits)
    return 'Phone number is too long. Must be no more than 15 digits after the + symbol (E.164 standard)';
  }

  // For US/Canada numbers (+1), enforce exactly 10 digits after country code
  if (trimmedPhone.startsWith('+1')) {
    if (trimmedPhone.length !== 12) { // +1 + 10 digits = 12 characters
      return 'US/Canada phone numbers must be exactly +1 followed by 10 digits (e.g., +15551234567)';
    }
  }

  return null;
};

/**
 * Validates ZIP code format
 * @param {string} zip - ZIP code to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validateZipCode = (zip) => {
  if (!zip) return null;

  const zipRegex = /^[0-9]{5}(?:-[0-9]{4})?$/;
  if (!zipRegex.test(zip)) {
    return 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
  }

  return null;
};

/**
 * Validates date format (YYYY-MM-DD)
 * @param {string} date - Date to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validateDate = (date) => {
  if (!date) return null;

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) {
    return 'Please enter a valid date in YYYY-MM-DD format';
  }

  // Check if it's a valid date
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) {
    return 'Please enter a valid date';
  }

  return null;
};

/**
 * Validates that the birth date indicates a person of at least 16 years old
 * @param {string} birthDate - Birth date to validate (YYYY-MM-DD format)
 * @returns {string|null} - Error message or null if valid
 */
export const validateBirthDateAge = (birthDate) => {
  if (!birthDate) return null;

  // First validate the date format
  const dateError = validateDate(birthDate);
  if (dateError) return dateError;

  const birthDateObj = parseISO(birthDate);
  const today = new Date();

  // Validate that the parsed date is valid
  if (!isValid(birthDateObj)) {
    return 'Please enter a valid date';
  }

  // Prevent future dates
  if (isAfter(birthDateObj, today)) {
    return 'Birth date cannot be in the future';
  }

  // Calculate age using date-fns
  const age = differenceInYears(today, birthDateObj);

  // Check minimum age requirement
  if (age < 16) {
    return 'Person must be at least 16 years old';
  }

  return null;
};

/**
 * Validates that a value is not empty
 * @param {string} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {string|null} - Error message or null if valid
 */
export const validateRequired = (value, fieldName) => {
  if (!value || value.trim() === '') {
    return `${fieldName} is required`;
  }
  return null;
};

// Lead validation rules configuration
// Note: email and phoneNumber are not individually required,
// but at least one must be provided (validated by createFormValidator)
export const LEAD_VALIDATION_RULES = {
  name: { required: true, message: 'Name is required' },
  email: { required: false, validator: validateEmail, message: 'Email is required' },
  phoneNumber: { required: false, validator: validatePhoneNumber },
  state: { required: true, message: 'State is required' },
  address: { required: true, message: 'Address is required' },
  zipCode: { required: true, validator: validateZipCode, message: 'ZIP code is required' },
  sourceOrgId: { required: true, message: 'Source organization is required' },
  birthDate: { required: false, validator: validateBirthDateAge },
};

// Field validator factory
export const createFieldValidator = (validationRules) => (field, value) => {
  const rule = validationRules[field];
  if (!rule) return null;

  const trimmedValue = typeof value === 'string' ? value.trim() : value;

  // Check required field
  if (rule.required && (!trimmedValue || trimmedValue === '')) {
    return rule.message;
  }

  // Check validator for non-empty values
  if (trimmedValue && rule.validator) {
    return rule.validator(trimmedValue);
  }

  return null;
};

// Form validator factory
export const createFormValidator = (validationRules: typeof LEAD_VALIDATION_RULES) => (formData: Record<string, any>) => {
  const errors: Record<string, string> = {};

  // First, validate individual fields
  Object.entries(validationRules).forEach(([field, rule]) => {
    const value = formData[field];
    const trimmedValue = typeof value === 'string' ? value.trim() : value;

    if (rule.required && (!trimmedValue || trimmedValue === '')) {
      errors[field] = ('message' in rule && rule.message) || `${field} is required`;
    } else if (trimmedValue && 'validator' in rule && rule.validator) {
      const error = rule.validator(trimmedValue);
      if (error) errors[field] = error;
    }
  });

  // Then, validate that at least one contact method is provided (email OR phone)
  const email = formData.email ? String(formData.email).trim() : '';
  const phoneNumber = formData.phoneNumber ? String(formData.phoneNumber).trim() : '';

  if (!email && !phoneNumber) {
    // Only show error if neither email nor phoneNumber have individual validation errors
    if (!errors.email && !errors.phoneNumber) {
      errors.email = 'At least one contact method is required: email or phone number';
    }
  }

  return errors;
};
